import {type TestIdProps, testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {type ComponentPropsWithRef, type FC, forwardRef, type ReactNode, type RefObject} from 'react'

import styles from './core.module.css'

export const SidebarFields: FC<{
  listRef: RefObject<HTMLDListElement | null>
  children: ReactNode
}> = ({children, listRef}) => <dl ref={listRef}>{children}</dl>

export const Field: FC<{label: string; children: React.ReactNode} & TestIdProps> = ({label, children, ...props}) => (
  // div is default for Box, but PRC could change in the future and this is _required_ to be a div by HTML spec
  <div className={styles.fieldContainer} {...props}>
    <FieldLabel>{label}</FieldLabel>
    <dd className={styles.fieldValueContainer}>{children}</dd>
  </div>
)

const FieldLabel: FC<{children: ReactNode}> = ({children}) => <dt className={styles.fieldLabel}>{children}</dt>

type FieldValueProps<T extends React.ElementType = 'div'> = {
  as?: T
  interactable?: boolean
  className?: string
  children?: ReactNode
} & Omit<ComponentPropsWithRef<T>, 'as' | 'interactable' | 'className'>

const FieldValueInternal = <T extends React.ElementType = 'div'>(
  {as, interactable = false, children, className, ...props}: FieldValueProps<T>,
  ref: React.ForwardedRef<React.ComponentRef<T>>,
) => {
  const Component = as || 'div'
  const componentProps = {
    ...props,
    ref,
    className: clsx(styles.interactiveFieldValue, interactable && styles.interactable, className),
    ...testIdProps('field-value'),
  }
  // @ts-expect-error - TypeScript has difficulty with polymorphic component props
  return <Component {...componentProps}>{children}</Component>
}

// @ts-expect-error - React 19 TypeScript has difficulty with polymorphic component props
export const FieldValue = forwardRef(FieldValueInternal) as <T extends React.ElementType = 'div'>(
  props: FieldValueProps<T> & {ref?: React.ForwardedRef<React.ComponentRef<T>>},
) => React.ReactElement | null

try{ Field.displayName ||= 'Field' } catch {}
try{ FieldLabel.displayName ||= 'FieldLabel' } catch {}
try{ FieldValueInternal.displayName ||= 'FieldValueInternal' } catch {}
try{ FieldValue.displayName ||= 'FieldValue' } catch {}